import { markRaw } from 'vue'
import { POKEDEX } from '~/data/pokedex'
import { getPokemonTypes } from '~/data/types'
import type { PokemonType } from '~/data/types'
import { getTypeEffectiveness } from '~/data/types'
import type { Rarity } from '~/data/gacha'
import { getPokemonPower } from '~/data/pokemonPower'
import type { OwnedPokemon } from '~/stores/useInventoryStore'
import {
  computeDescenteShardReward,
  DESCENTE_DAILY_SHARD_MULT,
} from '../../../shared/descente_sanctuaire_meta.mjs'

export { DESCENTE_DAILY_SHARD_MULT }

export const DESCENTE_RUN_VERSION = 2 as const
/**
 * Descente sans fin : plus de « boss final ». Le cap n’est qu’une borne technique
 * (bornage serveur / rendu de la piste), personne n’est censé l’atteindre.
 */
export const DESCENTE_MAX_FLOOR_CAP = 60
/** Palier de référence — franchi = « descente accomplie » (succès, titres, ascensions). */
export const DESCENTE_MILESTONE_FLOOR = 15
export const DESCENTE_REST_EVERY = 3
/** Un gardien tous les N étages : pic de difficulté qui ponctue la descente. */
export const DESCENTE_GUARDIAN_EVERY = 10
export const DESCENTE_FLOOR_TIMER_SEC = 48
export const DESCENTE_MAX_ASCENSION = 2 as const
export const DESCENTE_BASE_HEARTS = 3
/** Bornes hautes une fois toutes les améliorations permanentes prises. */
export const DESCENTE_MAX_HEARTS = 6
export const DESCENTE_MAX_REROLLS = 8

export type DescentePhase = 'draft' | 'fight' | 'rest'
export type DescenteAscension = 0 | 1 | 2
export type DescenteAffixId = 'brutal' | 'swift' | 'bulwark'
export type DescenteDirectorProfileId = 'balanced' | 'aggressive' | 'endurance'
export type DescenteBalancePresetId = 'safe_live' | 'hardcore_event'

export type DescenteModifierId =
  | 'dps_global_15'
  | 'first_hit_crit'
  | 'revive_plus1'
  | 'run_shard_20'
  | 'heal_between'
  | 'glass_cannon_30'
  | 'timer_bonus_6'
  | 'insurance_heart'

export interface DescenteModifierDef {
  id: DescenteModifierId
  nameFr: string
  nameEn: string
  descFr: string
  descEn: string
  tag: 'dps' | 'survival' | 'economy'
}

export const DESCENTE_MODIFIERS: Record<DescenteModifierId, DescenteModifierDef> = {
  dps_global_15: {
    id: 'dps_global_15',
    nameFr: 'Lame de cristal',
    nameEn: 'Crystal edge',
    descFr: '+15 % de dégâts pour cette descente.',
    descEn: '+15% damage for this descent.',
    tag: 'dps',
  },
  first_hit_crit: {
    id: 'first_hit_crit',
    nameFr: 'Première frappe',
    nameEn: 'First strike',
    descFr: 'La première salve de chaque combat inflige le double de dégâts.',
    descEn: 'The first hit each battle deals double damage.',
    tag: 'dps',
  },
  revive_plus1: {
    id: 'revive_plus1',
    nameFr: 'Rappel Max',
    nameEn: 'Max Revive',
    descFr: '+1 récupération : évite une défaite une fois (cœurs remis à 1).',
    descEn: '+1 recovery: avoid defeat once (hearts set to 1).',
    tag: 'survival',
  },
  run_shard_20: {
    id: 'run_shard_20',
    nameFr: 'Éclat bonus',
    nameEn: 'Bonus shard',
    descFr: '+20 % d’éclats du sanctuaire en fin de run.',
    descEn: '+20% Sanctum shards at run end.',
    tag: 'economy',
  },
  heal_between: {
    id: 'heal_between',
    nameFr: 'Soin de palier',
    nameEn: 'Floor recovery',
    descFr: 'Récupère 1 cœur après chaque combat (plafonné au max).',
    descEn: 'Heal 1 heart after each battle (capped at max).',
    tag: 'survival',
  },
  glass_cannon_30: {
    id: 'glass_cannon_30',
    nameFr: 'Cœur téméraire',
    nameEn: 'Reckless core',
    descFr: '+30 % dégâts, mais -1 cœur max (minimum 1).',
    descEn: '+30% damage, but -1 max heart (minimum 1).',
    tag: 'dps',
  },
  timer_bonus_6: {
    id: 'timer_bonus_6',
    nameFr: 'Sablier stable',
    nameEn: 'Stable hourglass',
    descFr: '+6 secondes au timer de chaque combat.',
    descEn: '+6 seconds to each battle timer.',
    tag: 'survival',
  },
  insurance_heart: {
    id: 'insurance_heart',
    nameFr: 'Égide du sanctuaire',
    nameEn: 'Sanctuary aegis',
    descFr: 'À 0 cœur, remonte à 1 cœur une fois.',
    descEn: 'At 0 hearts, restore to 1 heart once.',
    tag: 'survival',
  },
}

export const DESCENTE_MODIFIER_POOL: DescenteModifierId[] = [
  'dps_global_15',
  'first_hit_crit',
  'revive_plus1',
  'run_shard_20',
  'heal_between',
  'glass_cannon_30',
  'timer_bonus_6',
  'insurance_heart',
]

export interface DescenteEncounter {
  slug: string
  nameFr: string
  nameEn: string
  types: PokemonType[]
  maxHp: number
  isElite: boolean
  isGuardian?: boolean
  affixId?: DescenteAffixId | null
}

export interface DescenteSanctuaireRunState {
  v: typeof DESCENTE_RUN_VERSION
  seed: string
  ascension: DescenteAscension
  phase: DescentePhase
  floor: number
  /** Étage le plus profond atteint dans cette run (score de fin). */
  deepestFloor: number
  /**
   * Puissance brute (hors bonus de type) de l’équipe draftée, figée à la fin du draft.
   * Toute la difficulté est calée dessus : la descente vieillit avec le joueur.
   */
  powerBaseline: number
  /** Run lancée sur la seed du jour (classement quotidien + bonus d’éclats). */
  isDaily: boolean
  /** Multiplicateur de dégâts issu des améliorations permanentes, figé au lancement. */
  metaDpsMult: number
  /** Multiplicateur d’éclats issu des améliorations permanentes, figé au lancement. */
  metaShardMult: number
  /** Nombre de reliques proposées à chaque repos (améliorations permanentes). */
  restChoiceCount: number
  hearts: number
  maxHearts: number
  revivesLeft: number
  draftRound: number
  teamServerIds: number[]
  modifierIds: DescenteModifierId[]
  currentEncounter: DescenteEncounter | null
  enemyHp: number | null
  fightTimerLeft: number
  pendingRestChoices: DescenteModifierId[] | null
  firstHitCritUsed: boolean
  runGold: number
  /** Achats boutique in-run (bits, voir `descenteSanctuaireRunShop`). */
  runShopBits: number
  /** Multiplicateur DPS temporaire (combat en cours, achat boutique). */
  combatShopDpsMult: number
  rerollsLeft: number
  directorProfile: DescenteDirectorProfileId
  balancePreset: DescenteBalancePresetId
}

// `markRaw` empêche Vue de wrapper ces catalogues constants dans un proxy réactif s'ils
// transitent (directement ou indirectement) par un store Pinia. Évite que `traverse()` /
// `JSON.stringify` ne descende inutilement dans ces objets purs côté hot-path.
export const DESCENTE_AFFIXES: Record<
  DescenteAffixId,
  { nameFr: string; nameEn: string; hpMult: number; timerPenalty: number }
> = markRaw({
  brutal: { nameFr: 'Brutal', nameEn: 'Brutal', hpMult: 1.15, timerPenalty: 0 },
  swift: { nameFr: 'Vif', nameEn: 'Swift', hpMult: 1.0, timerPenalty: 4 },
  bulwark: { nameFr: 'Rempart', nameEn: 'Bulwark', hpMult: 1.25, timerPenalty: 2 },
})

export const DESCENTE_BALANCE = {
  byAscension: {
    0: { hpMult: 1.0, timerPenalty: 0, shardMult: 1.0, rerolls: 1 },
    1: { hpMult: 1.14, timerPenalty: 3, shardMult: 1.2, rerolls: 2 },
    2: { hpMult: 1.26, timerPenalty: 6, shardMult: 1.4, rerolls: 3 },
  } as const,
}

export const DESCENTE_BALANCE_PRESETS: Record<
  DescenteBalancePresetId,
  {
    nameFr: string
    nameEn: string
    hpMult: number
    timerDelta: number
    shardMult: number
  }
> = markRaw({
  safe_live: {
    nameFr: 'Safe Live',
    nameEn: 'Safe Live',
    hpMult: 1,
    timerDelta: 0,
    shardMult: 1,
  },
  hardcore_event: {
    nameFr: 'Hardcore Event',
    nameEn: 'Hardcore Event',
    hpMult: 1.18,
    timerDelta: -5,
    shardMult: 1.3,
  },
})

export const DESCENTE_DIRECTOR_PROFILES: Record<
  DescenteDirectorProfileId,
  {
    nameFr: string
    nameEn: string
    floorHpMult: number
    eliteEvery: number
    guardianMult: number
    affixWeights: Record<DescenteAffixId, number>
    bossTypeBias: PokemonType[]
  }
> = markRaw({
  balanced: {
    nameFr: 'Équilibré',
    nameEn: 'Balanced',
    floorHpMult: 1,
    eliteEvery: 5,
    guardianMult: 1.45,
    affixWeights: { brutal: 1, swift: 1, bulwark: 1 },
    bossTypeBias: ['normal', 'steel', 'dragon'],
  },
  aggressive: {
    nameFr: 'Agressif',
    nameEn: 'Aggressive',
    floorHpMult: 1.08,
    eliteEvery: 4,
    guardianMult: 1.55,
    affixWeights: { brutal: 2, swift: 2, bulwark: 1 },
    bossTypeBias: ['fire', 'fighting', 'electric'],
  },
  endurance: {
    nameFr: 'Endurance',
    nameEn: 'Endurance',
    floorHpMult: 1.14,
    eliteEvery: 6,
    guardianMult: 1.75,
    affixWeights: { brutal: 1, swift: 1, bulwark: 3 },
    bossTypeBias: ['rock', 'ground', 'steel'],
  },
})

const DIRECTOR_PROFILE_ORDER: DescenteDirectorProfileId[] = ['balanced', 'aggressive', 'endurance']

export function profileForSeed(seed: string): DescenteDirectorProfileId {
  const idx = hashSeedString(`${seed}:director`) % DIRECTOR_PROFILE_ORDER.length
  return DIRECTOR_PROFILE_ORDER[idx]!
}

function weightedAffix(rng: () => number, profile: DescenteDirectorProfileId): DescenteAffixId {
  const weights = DESCENTE_DIRECTOR_PROFILES[profile].affixWeights
  const entries: Array<{ id: DescenteAffixId; w: number }> = [
    { id: 'brutal', w: weights.brutal },
    { id: 'swift', w: weights.swift },
    { id: 'bulwark', w: weights.bulwark },
  ]
  const total = entries.reduce((a, b) => a + b.w, 0)
  let r = rng() * total
  for (const e of entries) {
    r -= e.w
    if (r <= 0) return e.id
  }
  return entries[0]!.id
}

export function balanceForAscension(ascension: DescenteAscension) {
  return DESCENTE_BALANCE.byAscension[ascension]
}

export function timerForRun(r: Pick<DescenteSanctuaireRunState, 'ascension' | 'modifierIds'>): number {
  const base = DESCENTE_FLOOR_TIMER_SEC - balanceForAscension(r.ascension).timerPenalty
  const bonus = r.modifierIds.includes('timer_bonus_6') ? 6 : 0
  return Math.max(26, base + bonus)
}

export function timerForRunWithPreset(
  r: Pick<DescenteSanctuaireRunState, 'ascension' | 'modifierIds' | 'balancePreset'>
): number {
  const preset = DESCENTE_BALANCE_PRESETS[r.balancePreset]
  return Math.max(20, timerForRun(r) + preset.timerDelta)
}

export function hashSeedString(s: string): number {
  let h = 2166136261 >>> 0
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i)
    h = Math.imul(h, 16777619)
  }
  return h >>> 0
}

/** RNG déterministe (mulberry32) pour ennemis / choix de draft cohérents avec la seed. */
export function createRng(seed: number): () => number {
  let t = seed >>> 0
  return () => {
    t += 0x6d2b79f5
    let r = Math.imul(t ^ (t >>> 15), 1 | t)
    r ^= r + Math.imul(r ^ (r >>> 7), 61 | r)
    return ((r ^ (r >>> 14)) >>> 0) / 4294967296
  }
}

const RARITY_WEIGHT: Record<Rarity, number> = {
  common: 1,
  rare: 2,
  epic: 5,
  mega: 7,
  gmax: 7,
  mythic: 8,
  legendary: 10,
  uber_mega: 12,
  divine: 15,
}

export function weightedPickTeamOptions(
  collection: OwnedPokemon[],
  excludeServerIds: Set<number>,
  rng: () => number,
  count = 3
): OwnedPokemon[] {
  const pool = collection.filter((p) => p.serverId != null && !excludeServerIds.has(p.serverId!))
  const picks: OwnedPokemon[] = []
  const used = new Set<number>()
  for (let n = 0; n < count && pool.length > 0; n++) {
    let total = 0
    const weights: number[] = []
    for (const p of pool) {
      if (used.has(p.serverId!)) {
        weights.push(0)
        continue
      }
      const w = RARITY_WEIGHT[p.rarity] ?? 1
      weights.push(w)
      total += w
    }
    if (total <= 0) break
    let r = rng() * total
    let idx = -1
    for (let i = 0; i < pool.length; i++) {
      if (used.has(pool[i]!.serverId!)) continue
      r -= weights[i] ?? 0
      if (r <= 0) {
        idx = i
        break
      }
    }
    if (idx < 0) idx = pool.findIndex((p) => !used.has(p.serverId!))
    const chosen = pool[idx]
    if (!chosen?.serverId) break
    used.add(chosen.serverId)
    picks.push(chosen)
  }
  const dedupe: OwnedPokemon[] = []
  const seen = new Set<number>()
  for (const p of picks) {
    if (p.serverId != null && !seen.has(p.serverId)) {
      seen.add(p.serverId)
      dedupe.push(p)
    }
  }
  return dedupe.slice(0, count)
}

export function pickRestChoices(rng: () => number, count = 3): DescenteModifierId[] {
  const pool = [...DESCENTE_MODIFIER_POOL]
  const wanted = Math.max(1, Math.min(pool.length, Math.floor(count)))
  const out: DescenteModifierId[] = []
  for (let i = 0; i < wanted; i++) {
    const j = Math.floor(rng() * pool.length)
    out.push(pool.splice(j, 1)[0]!)
  }
  return out
}

/** Clé de jour UTC — la seed du jour doit être identique pour tout le monde. */
export function descenteDailyKey(nowMs: number = Date.now()): string {
  return new Date(nowMs).toISOString().slice(0, 10)
}

export function descenteDailySeed(dayKey: string): string {
  return `sanctuaire-du-jour:${dayKey}`
}

export function isRestAfterFloor(floor: number): boolean {
  if (floor <= 0 || floor >= DESCENTE_MAX_FLOOR_CAP) return false
  return floor % DESCENTE_REST_EVERY === 0
}

/** Part du timer que l’étage est censé consommer, à puissance d’équipe nominale. */
const TTK_RATIO_START = 0.22
const TTK_RATIO_GROWTH = 1.075

/**
 * Fraction du timer qu’un étage doit coûter à une équipe sans bonus ni avantage de type.
 * Passé 1, l’étage n’est franchissable qu’avec des reliques, des améliorations ou un
 * bon matchup de types : c’est là que la descente commence à mordre.
 */
export function ttkRatioForFloor(floor: number): number {
  return TTK_RATIO_START * Math.pow(TTK_RATIO_GROWTH, Math.max(0, floor - 1))
}

/**
 * PV relatifs à la puissance de l’équipe draftée : la difficulté suit le joueur au lieu
 * d’une courbe absolue qui se périme dès la première équipe de fin de jeu.
 */
export function enemyHpForFloor(
  floor: number,
  powerBaseline: number,
  opts?: { isElite?: boolean; isGuardian?: boolean; guardianMult?: number }
): number {
  const baseline = Math.max(1, Math.floor(powerBaseline))
  let hp = baseline * DESCENTE_FLOOR_TIMER_SEC * ttkRatioForFloor(floor)
  if (opts?.isElite) hp *= 1.5
  if (opts?.isGuardian) hp *= opts.guardianMult ?? 1.5
  return Math.max(500, Math.min(1e15, Math.floor(hp)))
}

export function isGuardianFloor(floor: number): boolean {
  return floor > 0 && floor % DESCENTE_GUARDIAN_EVERY === 0
}

export function rollEncounter(
  seed: string,
  floor: number,
  powerBaseline: number,
  ascension: DescenteAscension,
  directorProfile: DescenteDirectorProfileId,
  rng: () => number
): DescenteEncounter {
  const h = hashSeedString(`${seed}:f${floor}`)
  const rng2 = createRng(h ^ Math.floor(rng() * 1e9))
  const profile = DESCENTE_DIRECTOR_PROFILES[directorProfile]
  const isGuardian = isGuardianFloor(floor)
  const isElite = !isGuardian && floor > 1 && floor % profile.eliteEvery === 0
  const bias = profile.bossTypeBias[Math.floor(rng2() * profile.bossTypeBias.length)]!
  const byType = POKEDEX.filter((p) => getPokemonTypes(p.slug).includes(bias))
  const pool = byType.length > 0 ? byType : POKEDEX
  const entry = pool[Math.floor(rng2() * pool.length)]!
  const affix: DescenteAffixId | null = floor >= 4 ? weightedAffix(rng2, directorProfile) : null
  const types = getPokemonTypes(entry.slug) as PokemonType[]
  const hpAsc = balanceForAscension(ascension).hpMult
  const hpAffix = affix ? DESCENTE_AFFIXES[affix].hpMult : 1
  const maxHp = Math.floor(
    enemyHpForFloor(floor, powerBaseline, {
      isElite,
      isGuardian,
      guardianMult: profile.guardianMult,
    }) *
      profile.floorHpMult *
      hpAsc *
      hpAffix
  )
  return {
    slug: entry.slug,
    nameFr: entry.nameFr,
    nameEn: entry.nameEn,
    types: types.length ? types : (['normal'] as PokemonType[]),
    maxHp,
    isElite,
    isGuardian,
    affixId: affix,
  }
}

export function applyPresetHp(maxHp: number, presetId: DescenteBalancePresetId): number {
  const p = DESCENTE_BALANCE_PRESETS[presetId]
  return Math.max(1, Math.floor(maxHp * p.hpMult))
}

export function modifierDpsMult(ids: DescenteModifierId[]): number {
  let m = 1
  if (ids.includes('dps_global_15')) m *= 1.15
  if (ids.includes('glass_cannon_30')) m *= 1.3
  return m
}

export function modifierShardMult(ids: DescenteModifierId[]): number {
  let m = 1
  if (ids.includes('run_shard_20')) m *= 1.2
  return m
}

/** Puissance brute de l’équipe, hors bonus de type : sert de référence à toute la difficulté. */
export function computeTeamPowerBaseline(team: OwnedPokemon[]): number {
  let sum = 0
  for (const poke of team) sum += getPokemonPower(poke)
  return Math.max(1, Math.round(sum))
}

export function computeTeamDpsVsTypes(
  team: OwnedPokemon[],
  enemyTypes: PokemonType[],
  modifierIds: DescenteModifierId[],
  opts?: { firstHitCrit?: boolean; combatShopDpsMult?: number; metaDpsMult?: number }
): number {
  const shopM = Math.max(1, Math.min(2, Number(opts?.combatShopDpsMult) || 1))
  const metaM = Math.max(1, Math.min(4, Number(opts?.metaDpsMult) || 1))
  const dpsM = modifierDpsMult(modifierIds) * shopM * metaM
  let sum = 0
  for (const poke of team) {
    const atkTypes = getPokemonTypes(poke)
    const typeMult =
      enemyTypes.length > 0
        ? Math.max(...atkTypes.map((atk) => getTypeEffectiveness(atk, enemyTypes)))
        : 1
    let permanent = getPokemonPower(poke)
    if (opts?.firstHitCrit && modifierIds.includes('first_hit_crit')) {
      permanent = Math.floor(permanent * 2)
    }
    sum += Math.round(permanent * typeMult * dpsM)
  }
  return Math.max(1, sum)
}

/**
 * Récompense d’une run : la formule vit dans `shared/descente_sanctuaire_meta.mjs`, importée
 * telle quelle par le serveur — l’affichage client et le plafond serveur ne peuvent pas diverger.
 */
export function computeShardReward(input: {
  floorReached: number
  modifierIds: DescenteModifierId[]
  ascension: DescenteAscension
  balancePreset: DescenteBalancePresetId
  metaShardMult?: number
  dailyBonus?: boolean
}): number {
  return computeDescenteShardReward(input)
}

export function parseDescenteRun(raw: unknown): DescenteSanctuaireRunState | null {
  if (!raw || typeof raw !== 'object') return null
  const o = raw as Record<string, unknown>
  if (Number(o.v) !== DESCENTE_RUN_VERSION) return null
  if (typeof o.seed !== 'string') return null
  const ph = o.phase
  if (ph !== 'draft' && ph !== 'fight' && ph !== 'rest') return null
  const phase: DescentePhase = ph
  const modifierIds = Array.isArray(o.modifierIds)
    ? (o.modifierIds.filter((id): id is DescenteModifierId =>
        typeof id === 'string' && (DESCENTE_MODIFIER_POOL as string[]).includes(id)
      ) as DescenteModifierId[])
    : []
  const teamServerIds = Array.isArray(o.teamServerIds)
    ? o.teamServerIds.map((x) => Math.floor(Number(x))).filter((n) => n > 0)
    : []
  const pending = o.pendingRestChoices
  let pendingRestChoices: DescenteSanctuaireRunState['pendingRestChoices'] = null
  if (Array.isArray(pending) && pending.length > 0) {
    const picks = (pending as unknown[]).filter(
      (x): x is DescenteModifierId =>
        typeof x === 'string' && (DESCENTE_MODIFIER_POOL as string[]).includes(x)
    )
    if (picks.length === pending.length) pendingRestChoices = picks
  }
  const enc = o.currentEncounter
  let currentEncounter: DescenteEncounter | null = null
  if (enc && typeof enc === 'object' && !Array.isArray(enc)) {
    const e = enc as Record<string, unknown>
    if (typeof e.slug === 'string') {
      currentEncounter = {
        slug: e.slug,
        nameFr: String(e.nameFr ?? ''),
        nameEn: String(e.nameEn ?? ''),
        types: Array.isArray(e.types) ? (e.types as PokemonType[]).slice(0, 2) : ['normal'],
        maxHp: Math.max(1, Math.floor(Number(e.maxHp) || 1)),
        isElite: Boolean(e.isElite),
        isGuardian: Boolean(e.isGuardian),
        affixId:
          e.affixId === 'brutal' || e.affixId === 'swift' || e.affixId === 'bulwark'
            ? e.affixId
            : null,
      }
    }
  }
  const floor = Math.max(1, Math.min(DESCENTE_MAX_FLOOR_CAP, Math.floor(Number(o.floor) || 1)))
  return {
    v: DESCENTE_RUN_VERSION,
    seed: o.seed,
    ascension: Math.max(0, Math.min(DESCENTE_MAX_ASCENSION, Math.floor(Number(o.ascension) || 0))) as DescenteAscension,
    phase,
    floor,
    deepestFloor: Math.max(
      0,
      Math.min(DESCENTE_MAX_FLOOR_CAP, Math.floor(Number(o.deepestFloor) || 0))
    ),
    powerBaseline: Math.max(0, Math.floor(Number(o.powerBaseline) || 0)),
    isDaily: Boolean(o.isDaily),
    metaDpsMult: Math.max(1, Math.min(4, Number(o.metaDpsMult) || 1)),
    metaShardMult: Math.max(1, Math.min(3, Number(o.metaShardMult) || 1)),
    restChoiceCount: Math.max(
      3,
      Math.min(DESCENTE_MODIFIER_POOL.length, Math.floor(Number(o.restChoiceCount) || 3))
    ),
    hearts: Math.max(0, Math.floor(Number(o.hearts) || 0)),
    maxHearts: Math.max(1, Math.min(DESCENTE_MAX_HEARTS, Math.floor(Number(o.maxHearts) || 3))),
    revivesLeft: Math.max(0, Math.floor(Number(o.revivesLeft) || 0)),
    draftRound: Math.max(0, Math.min(6, Math.floor(Number(o.draftRound) || 0))),
    teamServerIds,
    modifierIds,
    currentEncounter,
    enemyHp: o.enemyHp == null ? null : Math.max(0, Math.floor(Number(o.enemyHp))),
    fightTimerLeft: Math.max(0, Math.floor(Number(o.fightTimerLeft) || 0)),
    pendingRestChoices,
    firstHitCritUsed: Boolean(o.firstHitCritUsed),
    runGold: Math.max(0, Math.floor(Number(o.runGold) || 0)),
    runShopBits: Math.max(0, Math.floor(Number(o.runShopBits) || 0)),
    combatShopDpsMult: Math.max(1, Math.min(2, Number(o.combatShopDpsMult) || 1)),
    rerollsLeft: Math.max(0, Math.min(DESCENTE_MAX_REROLLS, Math.floor(Number(o.rerollsLeft) || 0))),
    directorProfile:
      o.directorProfile === 'aggressive' || o.directorProfile === 'endurance'
        ? o.directorProfile
        : 'balanced',
    balancePreset: o.balancePreset === 'hardcore_event' ? 'hardcore_event' : 'safe_live',
  }
}

export function sanctDraftRerollBonus(stats: Record<string, unknown> | undefined): number {
  if (!stats) return 0
  const bits = Math.floor(Number(stats._sanctShopBits ?? 0))
  if (!Number.isFinite(bits) || bits < 0) return 0
  return (bits & (1 << 5)) !== 0 ? 1 : 0
}

/** Effets des améliorations permanentes appliqués au lancement d’une run. */
export interface DescenteRunLoadout {
  extraRerolls?: number
  extraHearts?: number
  startGold?: number
  metaDpsMult?: number
  metaShardMult?: number
  restChoiceCount?: number
  isDaily?: boolean
}

export function newRunDraftState(
  seed: string,
  ascension: DescenteAscension = 0,
  loadout: DescenteRunLoadout = {}
): DescenteSanctuaireRunState {
  const directorProfile = profileForSeed(seed)
  const maxHearts = Math.min(
    DESCENTE_MAX_HEARTS,
    DESCENTE_BASE_HEARTS + Math.max(0, Math.floor(loadout.extraHearts ?? 0))
  )
  return {
    v: DESCENTE_RUN_VERSION,
    seed,
    ascension,
    phase: 'draft',
    floor: 1,
    deepestFloor: 0,
    powerBaseline: 0,
    isDaily: Boolean(loadout.isDaily),
    metaDpsMult: Math.max(1, Math.min(4, loadout.metaDpsMult ?? 1)),
    metaShardMult: Math.max(1, Math.min(3, loadout.metaShardMult ?? 1)),
    restChoiceCount: Math.max(
      3,
      Math.min(DESCENTE_MODIFIER_POOL.length, Math.floor(loadout.restChoiceCount ?? 3))
    ),
    hearts: maxHearts,
    maxHearts,
    revivesLeft: 0,
    draftRound: 0,
    teamServerIds: [],
    modifierIds: [],
    currentEncounter: null,
    enemyHp: null,
    fightTimerLeft: 0,
    pendingRestChoices: null,
    firstHitCritUsed: false,
    runGold: Math.max(0, Math.floor(loadout.startGold ?? 0)),
    runShopBits: 0,
    combatShopDpsMult: 1,
    rerollsLeft: Math.min(
      DESCENTE_MAX_REROLLS,
      balanceForAscension(ascension).rerolls + Math.max(0, Math.floor(loadout.extraRerolls ?? 0))
    ),
    directorProfile,
    balancePreset: 'safe_live',
  }
}
